import type { User } from "@amazing-care/types";
import * as React from "react";
import type { AuthAdapter, AuthState, SessionTokens } from "../types";

export interface AuthContextValue extends AuthState {
  adapter: AuthAdapter;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  signInWithMicrosoft: (options?: { next?: string }) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  handOffSession: () => Promise<SessionTokens | null>;
  setSession: (tokens: SessionTokens) => Promise<User>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  adapter: AuthAdapter;
  children: React.ReactNode;
}

export function AuthProvider({ adapter, children }: AuthProviderProps) {
  const [state, setState] = React.useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  React.useEffect(() => {
    let active = true;

    adapter.getUser().then((user) => {
      if (active) setState({ user, isLoading: false, isAuthenticated: !!user });
    });

    const unsubscribe = adapter.onAuthStateChange((user) => {
      if (active) setState({ user, isLoading: false, isAuthenticated: !!user });
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [adapter]);

  const value: AuthContextValue = React.useMemo(
    () => ({
      ...state,
      adapter,
      signInWithEmail: (email, password) => adapter.signInWithEmail(email, password),
      signInWithMicrosoft: (options) => adapter.signInWithMicrosoft(options),
      signOut: () => adapter.signOut(),
      resetPassword: (email) => adapter.resetPassword(email),
      handOffSession: () => adapter.handOffSession(),
      setSession: (tokens) => adapter.setSession(tokens),
    }),
    [state, adapter]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
